import { Select } from './Select';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface ActiveFilter {
  dimension: string;
  value: string;
}

interface FilterBarProps {
  siteKey: string;
  active: ActiveFilter | null;
  onChange: (filter: ActiveFilter | null) => void;
}

const DIMENSION_LABELS: Record<string, string> = {
  country: 'Country',
  device: 'Device',
  language: 'Language',
  channel_group: 'Channel',
  page_path: 'Page',
  landing_page: 'Landing page',
  day_of_week: 'Day',
  hour_of_day: 'Hour',
};

const label = (d: string) => DIMENSION_LABELS[d] ?? d.replace(/_/g, ' ');

/**
 * ONE filter row above everything it scopes.
 *
 * Deliberately not per-chart: scattered filters leave the reader unsure what any given card is
 * showing, and the method rules them out. Every chart below re-renders against the same slice.
 *
 * The options come from `GET /analytics/filters`, which reports only the dimensions that actually
 * have cross-tab rows behind them. Offering a filter with no cross-tab would return an empty page
 * that reads as "no traffic" when the truth is "not collected" — a far more misleading failure
 * than simply not offering it.
 */
export function FilterBar({ siteKey, active, onChange }: FilterBarProps) {
  const { data: filters, isError, isPending } = useQuery({
    queryKey: ['analytics-filters', siteKey],
    queryFn: () => api.analyticsFilters(siteKey),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const available = filters ?? [];

  if (isError) {
    return <div className="filterbar filterbar-empty t-label note-error">The filters could not be loaded — the pages below show every visitor.</div>;
  }

  if (isPending) return null;

  if (available.length === 0) {
    return (
      <div className="filterbar filterbar-empty t-label subtle">
        No filters available yet — they appear once GA4 has collected cross-tab data
        (country × page, device × page, language × page).
      </div>
    );
  }

  const selected = available.find((f) => f.dimension === active?.dimension);

  return (
    <div className="filterbar">
      <span className="t-label muted">Filter</span>

      <Select
        label="Dimension"
        inline
        value={active?.dimension ?? ''}
        placeholder="No filter"
        options={available.map((f) => ({ value: f.dimension, label: label(f.dimension) }))}
        onChange={(dimension) => {
          // Switching dimension clears the value: a country value is meaningless under "device",
          // and carrying it over would silently return nothing.
          onChange(dimension ? { dimension, value: '' } : null);
        }}
      />

      {selected && (
        <Select
          label={label(selected.dimension)}
          inline
          value={active?.value ?? ''}
          placeholder={`All ${label(selected.dimension).toLowerCase()}s`}
          // Value lists are the ones that grow — countries and pages will pass the
          // search threshold as soon as there is real traffic.
          options={selected.values.map((v) => ({ value: v, label: v || '(not set)' }))}
          onChange={(v) => onChange({ dimension: selected.dimension, value: v })}
        />
      )}

      {active?.value && (
        <>
          <button className="btn btn-ghost" onClick={() => onChange(null)}>Clear</button>
          {/* Said plainly, because a filtered page genuinely loses the Clarity columns. */}
          <span className="t-label subtle">
            Filtered views are GA4-only — Clarity cannot fund cross-tabs on 10 requests/day.
          </span>
        </>
      )}
    </div>
  );
}
